import { Link } from 'wouter';
import { Check, Heart, Play, Trophy } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import type { Game } from '@workspace/api-client-react';
import { difficultyFor } from '@/lib/difficulty';

interface GameCardProps {
  game: Game;
}

export function GameCard({ game }: GameCardProps) {
  return (
    <Link href={`/games/${game.slug}`}>
      <div className="group card-arcade block h-full w-full bg-card border border-primary/20 transition-all duration-300 hover:border-primary hover:-translate-y-2 hover:shadow-[0_10px_30px_-10px_rgba(0,217,255,0.4)] cursor-pointer z-10 flex flex-col">
        <div className="relative aspect-[4/3] w-full overflow-hidden bg-background border-b border-border/50">
          <div className="absolute inset-0 z-10 bg-gradient-to-t from-card to-transparent opacity-80" />
          {game.thumbnailUrl ? (
            <img
              src={game.thumbnailUrl}
              alt={game.title}
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110 opacity-90 group-hover:opacity-100"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-[hsl(240,15%,8%)]">
              <Play className="h-16 w-16 text-primary/20 group-hover:text-primary/60 transition-colors drop-shadow-[0_0_15px_rgba(0,217,255,0.5)]" />
            </div>
          )}
          
          <div className="absolute top-3 right-3 z-20 flex gap-2">
            {game.featured && (
              <Badge className="bg-secondary text-secondary-foreground rounded-none uppercase tracking-widest text-[10px] font-bold border-none glow-secondary px-2">
                Hot
              </Badge>
            )}
            <Badge variant="outline" className="bg-background/80 backdrop-blur text-primary border-primary/50 rounded-none uppercase tracking-widest text-[10px] font-bold px-2">
              {game.sport}
            </Badge>
            <Badge
              variant="outline"
              className={`${difficultyFor(game.gameType).className} rounded-none uppercase tracking-widest text-[10px] font-bold px-2`}
              data-testid={`badge-difficulty-${game.slug}`}
            >
              {difficultyFor(game.gameType).label}
            </Badge>
          </div>
          
          {/* Overlay text on hover */}
          <div className="absolute inset-0 z-20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-background/40 backdrop-blur-sm">
            <span className="font-display uppercase tracking-widest text-primary text-xl font-bold text-shadow-neon-cyan">Play Now</span>
          </div>
        </div>

        <div className="p-5 flex flex-col flex-1 relative z-10">
          <h3 className="font-display font-bold text-xl uppercase tracking-tight leading-none mb-3 group-hover:text-primary transition-colors">
            {game.title}
          </h3>

          <p className="text-sm text-muted-foreground line-clamp-2 mb-4 flex-1">
            {game.description}
          </p>

          <div className="mt-auto flex items-center justify-between border-t border-border/50 pt-4">
            <div className="flex items-center gap-4 text-xs font-mono text-muted-foreground font-bold tracking-wider">
              <span className="flex items-center gap-1.5 text-primary/80 group-hover:text-primary transition-colors">
                <Play className="h-3.5 w-3.5 fill-current" />
                {game.playCount.toLocaleString()}
              </span>
              <span className="flex items-center gap-1.5 text-secondary/80 group-hover:text-secondary transition-colors">
                <Heart className={`h-3.5 w-3.5 ${game.likeCount > 0 ? 'fill-current' : ''}`} />
                {game.likeCount.toLocaleString()}
              </span>
            </div>
            
            {game.hasDaily && (
              game.dailyCompleted ? (
                <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-green-400" data-testid="badge-daily-completed">
                  <Check className="h-3.5 w-3.5" />
                  Daily Done
                </div>
              ) : (
                <div className="text-[10px] font-bold uppercase tracking-widest text-primary animate-pulse">
                  Daily Ready · 2X PTS
                </div>
              )
            )}
          </div>
        </div>

        <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-primary/50 to-transparent transform -translate-x-full group-hover:animate-slide-right pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-[2px] h-full bg-gradient-to-b from-transparent via-secondary/50 to-transparent transform translate-y-full group-hover:animate-slide-up pointer-events-none" style={{animationDelay: '0.1s'}} />
      </div>
    </Link>
  );
}
